import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  HiArrowLeft,
  HiArrowRight,
  HiOutlineViewGrid,
  HiX,
  HiOutlineGift,
  HiOutlineIdentification,
  HiOutlineClipboardList,
  HiOutlineViewBoards,
  HiOutlinePencilAlt,
  HiOutlineCalendar,
  HiOutlineCreditCard,
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineCog,
  HiOutlineCash,
} from "react-icons/hi";
import CdnPreviewImage from "./CdnPreviewImage";
import { CustomButton } from "../../../atoms/CustomButton";
import { VERTICALS, VerticalDef } from "./verticalCatalog";
import { usePermissions } from "../../../../utils/permissions";
import useAuth from "../../../../hooks/useAuth";

type Choice = {
  key: string;
  icon: React.ComponentType<any>;
  route: string;
};

type Family = {
  key: string;
  icon: React.ComponentType<any>;
  accent: "purple" | "emerald" | "amber";
  choices: Choice[];
};

const FAMILIES: Family[] = [
  {
    key: "grow",
    icon: HiOutlineUsers,
    accent: "purple",
    choices: [
      {
        key: "leads",
        icon: HiOutlineClipboardList,
        route: "/control-panel/signup-forms",
      },
      {
        key: "reconnect",
        icon: HiOutlineUserGroup,
        route: "/control-panel/users",
      },
      { key: "loyalty", icon: HiOutlineGift, route: "/control-panel/lmc" },
    ],
  },
  {
    key: "operate",
    icon: HiOutlineCog,
    accent: "emerald",
    choices: [
      {
        key: "board",
        icon: HiOutlineViewBoards,
        route: "/control-panel/todos",
      },
      {
        key: "esign",
        icon: HiOutlinePencilAlt,
        route: "/control-panel/e-signature",
      },
    ],
  },
  {
    key: "earn",
    icon: HiOutlineCash,
    accent: "amber",
    choices: [
      {
        key: "payments",
        icon: HiOutlineCreditCard,
        route: "/control-panel/payment-module",
      },
      {
        key: "events",
        icon: HiOutlineCalendar,
        route: "/control-panel/event-payments",
      },
    ],
  },
];

const NEUTRAL_ACCENT = {
  iconBg: "bg-gray-50",
  iconText: "text-gray-600",
  ring: "focus:ring-gray-400",
  hoverBorder: "hover:border-gray-300",
};

const ACCENT: Record<
  Family["accent"],
  { iconBg: string; iconText: string; ring: string; hoverBorder: string }
> = {
  purple: NEUTRAL_ACCENT,
  emerald: NEUTRAL_ACCENT,
  amber: NEUTRAL_ACCENT,
};

type HubView = "intentions" | "sectors" | "suggestions";

const WelcomeHub: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuth();
  const permissions = usePermissions(user);

  const [view, setView] = useState<HubView>("intentions");
  const [familyKey, setFamilyKey] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<VerticalDef["slug"] | null>(
    null
  );

  const family = FAMILIES.find((f) => f.key === familyKey) || null;
  const selectedVertical =
    VERTICALS.find((v) => v.slug === selectedSlug) || null;

  const goToIntentions = () => {
    setView("intentions");
    setFamilyKey(null);
  };

  const handleSectorClick = (slug: VerticalDef["slug"]) => {
    setSelectedSlug(slug);
    setView("suggestions");
  };

  const handleExploreAll = () => {
    goToIntentions();
  };

  if (!permissions.isOwner()) return null;

  if (view === "sectors" || view === "suggestions") {
    return (
      <div className="w-full bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {view === "suggestions" && selectedVertical
                ? t(
                    `dashboard.verticalTunnel.verticals.${selectedVertical.slug}.label`
                  )
                : t("dashboard.verticalTunnel.routing.title")}
            </h2>
            <p className="text-sm text-gray-500">
              {view === "suggestions"
                ? t("dashboard.verticalTunnel.suggestions.subtitle")
                : t("dashboard.verticalTunnel.routing.subtitle")}
            </p>
          </div>
          <CustomButton
            type="button"
            variant="none"
            size="icon"
            onClick={goToIntentions}
            aria-label={t("dashboard.verticalTunnel.close") as string}
            className="flex-shrink-0 rounded-md text-gray-400 enabled:hover:bg-gray-100 enabled:hover:text-gray-700"
            icon={<HiX className="w-5 h-5" />}
          />
        </div>

        {view === "sectors" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {VERTICALS.map((v) => {
              const Icon = v.icon;
              return (
                <button
                  key={v.slug}
                  type="button"
                  onClick={() => handleSectorClick(v.slug)}
                  className="group text-left flex items-center gap-3 rounded-lg border border-gray-200 hover:border-gray-300 bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-50 group-hover:bg-gray-100 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 font-figtree">
                      {t(`dashboard.verticalTunnel.verticals.${v.slug}.label`)}
                    </p>
                    <p className="text-xs text-gray-500 font-figtree truncate">
                      {t(`dashboard.verticalTunnel.verticals.${v.slug}.hint`)}
                    </p>
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              onClick={handleExploreAll}
              className="group text-left flex items-center gap-3 rounded-lg border border-dashed border-gray-300 hover:border-gray-400 bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-50 group-hover:bg-gray-100 flex items-center justify-center flex-shrink-0 transition-colors">
                <HiOutlineViewGrid className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 font-figtree">
                  {t("dashboard.verticalTunnel.otherTile.label")}
                </p>
                <p className="text-xs text-gray-500 font-figtree truncate">
                  {t("dashboard.verticalTunnel.otherTile.hint")}
                </p>
              </div>
            </button>
          </div>
        )}

        {view === "suggestions" && selectedVertical && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 lg:order-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 font-figtree mb-3">
                  {t(
                    `dashboard.verticalTunnel.preview.${selectedVertical.preview}`
                  )}
                </p>
                <div className="flex justify-center lg:justify-start">
                  <CdnPreviewImage kind={selectedVertical.preview} />
                </div>
              </div>

              <div className="lg:col-span-3 lg:order-1 flex flex-col gap-4">
                {selectedVertical.suggestions.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => navigate(s.route)}
                      className="group text-left flex items-center gap-4 rounded-lg border border-gray-200 hover:border-gray-300 bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gray-50 group-hover:bg-gray-100 flex items-center justify-center flex-shrink-0 transition-colors">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 font-figtree">
                            {t(
                              `dashboard.verticalTunnel.suggestions.${selectedVertical.slug}.${s.key}.title`
                            )}
                          </p>
                          {s.badge === "free" && (
                            <span className="px-1.5 py-0.5 rounded border text-xs font-medium bg-green-50 text-green-700 border-green-200">
                              {t("dashboard.verticalTunnel.badges.free")}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 font-figtree mt-0.5">
                          {t(
                            `dashboard.verticalTunnel.suggestions.${selectedVertical.slug}.${s.key}.subtitle`
                          )}
                        </p>
                      </div>
                      <HiArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <CustomButton
                type="button"
                variant="none"
                size="sm"
                onClick={() => setView("sectors")}
                className="inline-flex gap-1.5 px-0 font-figtree font-medium text-gray-500 enabled:hover:text-gray-800"
                icon={<HiArrowLeft className="w-4 h-4" />}
                title={t("dashboard.verticalTunnel.back")}
              />
              <CustomButton
                type="button"
                variant="none"
                size="sm"
                onClick={goToIntentions}
                className="px-0 font-figtree font-medium text-gray-500 enabled:hover:text-gray-800"
                title={t("dashboard.verticalTunnel.suggestions.seeAll")}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  if (family) {
    const a = ACCENT[family.accent];
    return (
      <div className="w-full bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t(`dashboard.intentionTunnel.families.${family.key}.stepTitle`)}
          </h2>
          <p className="text-sm text-gray-500">
            {t(`dashboard.intentionTunnel.families.${family.key}.stepSubtitle`)}
          </p>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
            family.choices.length >= 3 ? "lg:grid-cols-3" : ""
          }`}
        >
          {family.choices.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => navigate(c.route)}
                className={`group text-left flex items-center gap-4 rounded-lg border border-gray-200 ${a.hoverBorder} bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${a.ring}`}
              >
                <div
                  className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`w-5 h-5 ${a.iconText}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 font-figtree">
                    {t(`dashboard.intentionTunnel.choices.${c.key}.label`)}
                  </p>
                  <p className="text-xs text-gray-500 font-figtree mt-0.5">
                    {t(
                      `dashboard.intentionTunnel.choices.${c.key}.description`
                    )}
                  </p>
                </div>
                <HiArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
              </button>
            );
          })}
        </div>

        <CustomButton
          type="button"
          variant="none"
          size="sm"
          onClick={() => setFamilyKey(null)}
          className="mt-5 inline-flex gap-1.5 px-0 font-figtree font-medium text-gray-500 enabled:hover:text-gray-800"
          icon={<HiArrowLeft className="w-4 h-4" />}
          title={t("dashboard.intentionTunnel.back")}
        />
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-gray-500 font-figtree">
          {t("dashboard.intentionTunnel.compactTitle")}
        </p>
        <button
          type="button"
          onClick={() => setView("sectors")}
          className="flex-shrink-0 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 font-figtree transition-colors"
        >
          {t("dashboard.intentionTunnel.compactExploreAll")}
          <HiArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => navigate("/control-panel/business-card")}
          className={`group text-left flex items-center gap-3 rounded-lg border border-gray-200 ${ACCENT.purple.hoverBorder} bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${ACCENT.purple.ring}`}
        >
          <div
            className={`w-10 h-10 rounded-lg ${ACCENT.purple.iconBg} flex items-center justify-center flex-shrink-0`}
          >
            <HiOutlineIdentification
              className={`w-5 h-5 ${ACCENT.purple.iconText}`}
            />
          </div>
          <span className="flex-1 text-sm font-semibold text-gray-900 font-figtree">
            {t("dashboard.intentionTunnel.compactCard")}
          </span>
          <HiArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
        </button>
        {FAMILIES.map((f) => {
          const Icon = f.icon;
          const a = ACCENT[f.accent];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFamilyKey(f.key)}
              className={`group text-left flex items-center gap-3 rounded-lg border border-gray-200 ${a.hoverBorder} bg-white p-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${a.ring}`}
            >
              <div
                className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}
              >
                <Icon className={`w-5 h-5 ${a.iconText}`} />
              </div>
              <span className="flex-1 text-sm font-semibold text-gray-900 font-figtree">
                {t(`dashboard.intentionTunnel.families.${f.key}.label`)}
              </span>
              <HiArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WelcomeHub;
